import { FieldType } from '@databases/model/field';
import {
  type AiTextValue,
  type AiValueMetadata,
  type SingleTextValue,
  type ValueJSON
} from '@databases/model/value';
import * as Y from 'yjs';

import { isAiActor } from '../ai-metadata';
import { YFieldType } from '../y-field';

import { writeAdfToXmlFragment } from './adf-xml-conversion';
import { TextValueStorageKeys, YTextValue } from './y-text-value';

export const AiValueMetadataStorageKeys = {
  lastUpdatedAt: 'ai_lua',
  lastUpdatedBy: 'ai_lub',
  lastUpdatedUserAccountId: 'ai_lub_aid'
} as const;

type AiTextLikeFieldType = FieldType.AiSummary;

const hasMetadata = (metadata: AiValueMetadata): boolean =>
  metadata.lastUpdatedAt !== undefined ||
  metadata.lastUpdatedBy !== undefined ||
  metadata.lastUpdatedUserAccountId !== undefined;

const readString = (input: Y.Map<any>, key: string): string | undefined => {
  const value = input.get(key);
  return typeof value === 'string' ? value : undefined;
};

const readAiValueMetadata = (input: Y.Map<any>): AiValueMetadata | undefined => {
  const lastUpdatedBy = input.get(AiValueMetadataStorageKeys.lastUpdatedBy);
  const metadata = {
    lastUpdatedAt: readString(input, AiValueMetadataStorageKeys.lastUpdatedAt),
    lastUpdatedBy: isAiActor(lastUpdatedBy) ? lastUpdatedBy : undefined,
    lastUpdatedUserAccountId: readString(
      input,
      AiValueMetadataStorageKeys.lastUpdatedUserAccountId
    )
  };

  return hasMetadata(metadata) ? metadata : undefined;
};

const setOptionalString = (
  input: Y.Map<any>,
  key: string,
  value: string | undefined
): void => {
  if (value === undefined) {
    input.delete(key);
  } else {
    input.set(key, value);
  }
};

export class YAITextValue<
  T extends AiTextLikeFieldType = FieldType.AiSummary
> extends YTextValue<T> {
  constructor(
    private aiInput: Y.Map<any>,
    attach?: (map: Y.Map<any>) => void,
    fieldType: T = FieldType.AiSummary as T
  ) {
    super(aiInput, attach, fieldType);
  }

  get metadata(): AiValueMetadata | undefined {
    return readAiValueMetadata(this.aiInput);
  }

  override get value(): AiTextValue {
    const metadata = this.metadata;
    return {
      ...super.value,
      ...(metadata !== undefined && { metadata })
    };
  }

  setMetadata(metadata: AiValueMetadata): void {
    setOptionalString(
      this.aiInput,
      AiValueMetadataStorageKeys.lastUpdatedAt,
      metadata.lastUpdatedAt
    );

    if (metadata.lastUpdatedBy === undefined) {
      this.aiInput.delete(AiValueMetadataStorageKeys.lastUpdatedBy);
    } else {
      this.aiInput.set(AiValueMetadataStorageKeys.lastUpdatedBy, metadata.lastUpdatedBy);
    }

    setOptionalString(
      this.aiInput,
      AiValueMetadataStorageKeys.lastUpdatedUserAccountId,
      metadata.lastUpdatedUserAccountId
    );
  }

  clearMetadata(): void {
    this.aiInput.delete(AiValueMetadataStorageKeys.lastUpdatedAt);
    this.aiInput.delete(AiValueMetadataStorageKeys.lastUpdatedBy);
    this.aiInput.delete(AiValueMetadataStorageKeys.lastUpdatedUserAccountId);
  }

  setAIGeneratedContent(value: SingleTextValue, date = new Date()): void {
    this.setTextContent(value);
    this.setMetadata({
      lastUpdatedAt: date.toISOString(),
      lastUpdatedBy: 'ai'
    });
  }

  markUserUpdated(accountId: string | undefined, date = new Date()): void {
    this.setMetadata({
      lastUpdatedAt: date.toISOString(),
      lastUpdatedBy: 'user',
      ...(accountId !== undefined && { lastUpdatedUserAccountId: accountId })
    });
  }

  override toJSON(): ValueJSON<T, AiTextValue> {
    return {
      type: this.type,
      value: this.value
    };
  }

  static fromAiTextJSON<T extends AiTextLikeFieldType>(json: ValueJSON<T, AiTextValue>) {
    const metadata = json.value.metadata;
    const entries: Array<[string, unknown]> = [
      [TextValueStorageKeys.type, YFieldType.AI_SUMMARY],
      [TextValueStorageKeys.plainText, new Y.Text(json.value.plain ?? '')]
    ];

    if (json.value.rich !== undefined) {
      const fragment = new Y.XmlFragment();
      writeAdfToXmlFragment(json.value.rich, fragment);
      entries.push([TextValueStorageKeys.richText, fragment]);
    }

    if (metadata?.lastUpdatedAt !== undefined) {
      entries.push([AiValueMetadataStorageKeys.lastUpdatedAt, metadata.lastUpdatedAt]);
    }

    if (metadata?.lastUpdatedBy !== undefined) {
      entries.push([AiValueMetadataStorageKeys.lastUpdatedBy, metadata.lastUpdatedBy]);
    }

    if (metadata?.lastUpdatedUserAccountId !== undefined) {
      entries.push([
        AiValueMetadataStorageKeys.lastUpdatedUserAccountId,
        metadata.lastUpdatedUserAccountId
      ]);
    }

    const map = new Y.Map(entries);

    return new YAITextValue(map, undefined, json.type);
  }

  private setTextContent(value: SingleTextValue): void {
    if (value.rich !== undefined) {
      this.setContent(value.rich);
      return;
    }

    const existingLength = this.plainTextValue.length;
    if (existingLength > 0) {
      this.delete(0, existingLength);
    }

    if (value.plain) {
      this.insert(0, value.plain);
    }
  }
}
